import { outOfBandThemeColors } from '@/theme/colors';
import CloseIcon from '@mui/icons-material/Close';
import { Modal, styled } from '@mui/material';
import { useState } from 'react';

import { FF_ENABLE_NOTICE_ON_MODAL, FF_WOM_TNC_COPY } from '@/constants/featureFlags';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useGoogleAnalytics from '@/hooks/useGoogleAnalytics';
import { useWomPricing } from '@/hooks/useWomPricing';

import { GoogleAnalyticsEventName } from '@montugroup/circuit-shared/types/analytics';
import { Box, Button, Link, Typography } from '@montugroup/design-system';

const ModalContainer = styled(Box)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 2rem)',
  maxWidth: '340px',
  borderRadius: '4px',
  backgroundColor: theme.palette.background.paper,
  padding: '2rem 1.5rem',
  boxShadow: '0px 11px 15px -7px #00000033',
  [theme.breakpoints.up('md')]: {
    maxWidth: '500px',
    padding: '2rem 1.9rem',
  },
}));

const ModalHeader = styled(Typography)(() => ({
  textAlign: 'center',
  fontSize: '1.5rem',
  padding: '1rem 2rem',
  fontWeight: 600,
  color: outOfBandThemeColors.alternaleaf.deepNavy,
}));

const ImgContainer = styled(Typography)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

function PatientLoginModal() {
  const [open, setOpen] = useState(true);
  const { sendGoogleAnalyticsGTagEvent } = useGoogleAnalytics();
  const { flags } = useFeatureFlags();
  const isNoticeEnabled = flags[FF_ENABLE_NOTICE_ON_MODAL];
  const {
    discount: womPrice,
    baseDiscount,
    consultPrice,
    priceAfterDiscount,
    isOverrideActive,
    copyVariant,
  } = useWomPricing();
  const womTncCopy = flags[FF_WOM_TNC_COPY];
  // An override only lasts until its end date, so its copy says the offer is limited time
  const limitedTime = isOverrideActive ? ' for a limited time' : '';
  const regularCopy =
    priceAfterDiscount === 0
      ? `Help your friends and family access quality healthcare with a free initial consultation${limitedTime} (usually $${consultPrice}). Login to reveal your unique code.`
      : `Help your friends and family access quality healthcare with a $${womPrice} discount on their initial consultation${limitedTime}. Only $${priceAfterDiscount} instead of $${consultPrice}. Login to reveal your unique code.`;

  const onClose = (_event: MouseEvent, reason: string) => {
    if (reason === 'backdropClick') {
      sendGoogleAnalyticsGTagEvent(GoogleAnalyticsEventName.LOGIN_MODAL_OUTSIDE_CLICK, {
        event_category: 'login_modal_wom_campaign',
        event_label: 'modal_outside_click',
      });
    }
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="login-patient-referral-modal"
      aria-describedby="modal used to introduct word of mouth referral to user"
    >
      <ModalContainer>
        <Button
          aria-label="close"
          onClick={() => {
            sendGoogleAnalyticsGTagEvent(GoogleAnalyticsEventName.LOGIN_MODAL_CLOSE_CLICK, {
              event_category: 'login_modal_wom_campaign',
              event_label: 'modal_close_click',
            });
            setOpen(false);
          }}
          variant="text"
          sx={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            color: (theme) => theme.palette.grey[500],
          }}
          data-testid="close-modal-button"
        >
          <CloseIcon />
        </Button>
        {!isNoticeEnabled && (
          <ImgContainer>
            <img src="/assets/images/word_of_mouth.png" alt="Circuit" />
          </ImgContainer>
        )}
        <ModalHeader id="refer-modal-title" variant="h4">
          {isNoticeEnabled ? 'Scheduled Maintenance Notice' : 'Share the Care'}
        </ModalHeader>
        <Typography
          textAlign="center"
          marginBottom="1.5rem"
          sx={{
            whiteSpace: 'pre-line',
          }}
        >
          {isNoticeEnabled
            ? 'Please be advised that the Alternaleaf Patient Portal will be unavailable tonight between approximately 11 PM and 1 AM AEST, due to scheduled maintenance. \n\nPatients may not be able to log in to their accounts during this time. \n\nWe apologise for any inconvenience and thank you for your patience. '
            : copyVariant === 'increased'
              ? `We’ve increased the Share the Care discount from $${baseDiscount} to $${womPrice}${limitedTime}, helping your friends and family access quality healthcare at our lowest price of $${priceAfterDiscount} for their initial consultation (usually $${consultPrice}). Login to reveal your unique code.`
              : regularCopy}
        </Typography>

        <Button
          fullWidth
          sx={(theme) => ({
            margin: '1rem 0',
            boxShadow: '0px 3px 1px -2px #00000033',
            letterSpacing: '1px',
            padding: `${theme.spacing(4)} !important`,
            fontWeight: `${theme.typography.fontWeightBold} !important`,
            fontSize: '1.1rem !important',
          })}
          onClick={() => {
            sendGoogleAnalyticsGTagEvent(GoogleAnalyticsEventName.LOGIN_MODAL_CONTINUE_CLICK, {
              event_category: 'login_modal_wom_campaign',
              event_label: 'modal_continue_click',
            });
            setOpen(false);
          }}
          variant="contained"
        >
          Continue to login
        </Button>
        {!isNoticeEnabled && (
          <>
            <Typography
              sx={{
                fontSize: '12px',
                textAlign: 'center',
                width: '100%',
                color: outOfBandThemeColors.alternaleaf.deepNavy,
              }}
            >
              {womTncCopy} Further terms & conditions apply:
              <Link
                href="https://www.alternaleaf.com.au/share-terms"
                sx={{
                  textDecoration: 'underline',
                  color: (theme) => theme.palette.text.primary,
                  marginLeft: (theme) => theme.spacing(1),
                  lineHeight: '0.7',
                }}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  sendGoogleAnalyticsGTagEvent(GoogleAnalyticsEventName.STC_TERMS_CONDITIONS, {
                    event_category: 'stc_terms_conditions',
                    event_label: 'terms_conditions_link',
                  })
                }
              >
                View here
              </Link>
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                textAlign: 'center',
                width: '100%',
                color: outOfBandThemeColors.alternaleaf.deepNavy,
              }}
            >
              Our healthcare services may not be suitable for everyone. Speak to one of our health professionals to
              determine whether our services are appropriate. Treatment can affect every person differently, due to
              their personal circumstances.
            </Typography>
          </>
        )}
      </ModalContainer>
    </Modal>
  );
}

export default PatientLoginModal;
